import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, DialogContent, Grid, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { isSupported as isBrowserSupported } from 'twilio-video';

import DialogHeader from 'components/DialogHeader';
import { useStyles } from 'components/DialogVideoOnDemandSessionCreation/components/SessionLinkStep/SessionLinkStep.useStyles';
import { SESSION_TYPE } from 'components/DialogVideoOnDemandSessionCreation/DialogVideoOnDemandSessionCreation';
import TelehealthBrowserNotSupportedBanner from 'components/TelehealthBrowserNotSupportedBanner';
import TelehealthSessionLink from 'components/TelehealthSessionLink';

import messages from './messages';

interface SessionLinkStepProps {
  classes: any;
  formProps: any;
  isProvider: boolean;
}

const SessionLinkStep: React.FC<SessionLinkStepProps> = ({ classes, formProps, isProvider }) => {
  const [copiedLinks, setCopiedLinks] = useState<any>({ patient: false, provider: false, guest: false, zoom: false });
  const {
    values: { data, sessionType },
  } = formProps;
  const { formatMessage } = useIntl();

  const title =
    isProvider || sessionType === SESSION_TYPE.telehealth ? 'Invitation sent!' : 'Video call invitation sent!';
  const showProviderLink = !isProvider && !data.zoomUrl && data.providerUrl && sessionType === SESSION_TYPE.telehealth;
  const showJoinVideoButton = isProvider || sessionType === SESSION_TYPE.video;

  const handleJoinSession = () => {
    const { zoomUrl, providerUrl, staffUrl } = data;

    if (zoomUrl) {
      window.open(zoomUrl, '_blank');
    } else {
      window.open(isProvider ? providerUrl : staffUrl, '_blank');
    }
  };

  return (
    <>
      <DialogHeader title={title} />
      {isProvider && <TelehealthBrowserNotSupportedBanner />}
      <DialogContent className={classes.content}>
        <Grid container spacing={16}>
          {showJoinVideoButton && (
            <Grid item xs={12}>
              <Button
                color="primary"
                fullWidth
                onClick={handleJoinSession}
                variant="contained"
                disabled={!isBrowserSupported}
                data-lh-id="telehealth-on-demand-session-join-button"
              >
                Join Session
              </Button>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography className={classes.text} component="div">
              {`Shareable link${showProviderLink ? 's' : ''}`}
            </Typography>
          </Grid>
          {data.zoomUrl ? (
            <Grid item xs={12}>
              <TelehealthSessionLink
                label={formatMessage(messages.zoom)}
                copied={copiedLinks.zoom}
                onCopy={() => setCopiedLinks({ patient: false, provider: false, guest: false, zoom: true })}
                url={data.zoomUrl}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <TelehealthSessionLink
                  label={formatMessage(messages.patient)}
                  copied={copiedLinks.patient}
                  onCopy={() => setCopiedLinks({ patient: true, provider: false, guest: false, zoom: false })}
                  url={data.patientUrl}
                />
              </Grid>
              {showProviderLink && (
                <Grid item xs={12}>
                  <TelehealthSessionLink
                    label={formatMessage(messages.provider)}
                    copied={copiedLinks.provider}
                    onCopy={() => setCopiedLinks({ patient: false, provider: true, guest: false, zoom: false })}
                    url={data.providerUrl}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TelehealthSessionLink
                  label={formatMessage(messages.guest)}
                  copied={copiedLinks.guest}
                  onCopy={() => setCopiedLinks({ patient: false, provider: false, guest: true, zoom: false })}
                  url={data.guestUrl}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
    </>
  );
};

export default withStyles(useStyles)(SessionLinkStep);
